/** @format */
"use client";

import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Music } from "lucide-react";

interface TempoPreset {
  name: string;
  bpm: number;
}
interface RhythmPattern {
  name: string;
  subdivision: number;
  displayName: string;
}

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const PRESET_TEMPOS: TempoPreset[] = [
  { name: "Largo", bpm: 50 },
  { name: "Adagio", bpm: 72 },
  { name: "Andante", bpm: 90 },
  { name: "Moderato", bpm: 120 },
  { name: "Allegro", bpm: 156 },
  { name: "Presto", bpm: 200 },
];

const RHYTHM_PATTERNS: RhythmPattern[] = [
  { name: "quarter", subdivision: 1, displayName: "♩ Nốt đen" },
  { name: "eighth", subdivision: 2, displayName: "♪ Móc đơn" },
  { name: "triplet", subdivision: 3, displayName: "³ Bộ ba" },
  { name: "sixteenth", subdivision: 4, displayName: "♬ Móc kép" },
];

const TIME_SIGS: number[] = [2, 3, 4, 6];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const MetronomeClient: React.FC = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [bpm, setBpm] = useState(120);
  const [beats, setBeats] = useState(4);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [subdivision, setSubdivision] = useState(1);
  const [currentSubBeat, setCurrentSubBeat] = useState(0);
  const [bpmInput, setBpmInput] = useState("120");
  const [countIn, setCountIn] = useState<number | null>(null); // null = not counting, 1..beats = counting

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const beatCount = useRef(0);
  const subBeatCount = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AC();
      } catch (e) {
        console.warn("Web Audio API not supported", e);
      }
    }
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  // Preload voices ref
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const loadVoices = () => {
      voicesRef.current = window.speechSynthesis.getVoices();
    };
    loadVoices();
    window.speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () =>
      window.speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, []);

  // Female voice countdown using Web Speech API


  const playSound = (isAccent = false, isSubBeat = false) => {
    if (!audioContextRef.current || isMuted) return;
    try {
      const osc = audioContextRef.current.createOscillator();
      const gain = audioContextRef.current.createGain();
      osc.connect(gain);
      gain.connect(audioContextRef.current.destination);
      osc.frequency.setValueAtTime(
        isAccent ? 900 : isSubBeat ? 500 : 700,
        audioContextRef.current.currentTime,
      );
      osc.type = "sine";
      const vol = isAccent
        ? volume * 0.9
        : isSubBeat
          ? volume * 0.35
          : volume * 0.6;
      gain.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gain.gain.linearRampToValueAtTime(
        vol,
        audioContextRef.current.currentTime + 0.005,
      );
      gain.gain.exponentialRampToValueAtTime(
        0.001,
        audioContextRef.current.currentTime + 0.12,
      );
      osc.start(audioContextRef.current.currentTime);
      osc.stop(audioContextRef.current.currentTime + 0.12);
    } catch (e) {
      console.warn("Sound error", e);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      const interval = 60000 / (bpm * subdivision);
      const playBeat = () => {
        const isMainBeat = subBeatCount.current % subdivision === 0;
        if (isMainBeat) {
          beatCount.current = Math.floor(subBeatCount.current / subdivision);
          setCurrentBeat(beatCount.current);
          // Speak beat number: 1-indexed for natural counting
        }
        setCurrentSubBeat(subBeatCount.current);
        playSound(subBeatCount.current === 0, !isMainBeat);
        subBeatCount.current =
          (subBeatCount.current + 1) % (beats * subdivision);
      };
      playBeat();
      intervalRef.current = setInterval(playBeat, interval);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, bpm, beats, volume, isMuted, subdivision]);

  const countInRef = useRef<NodeJS.Timeout | null>(null);

  const handlePlayPause = () => {
    if (isPlaying) {
      // STOP
      setIsPlaying(false);
      setCountIn(null);
      if (countInRef.current) clearInterval(countInRef.current);
      if (typeof window !== "undefined" && window.speechSynthesis)
        window.speechSynthesis.cancel();
      return;
    }

    // Resume audio context if suspended
    if (audioContextRef.current?.state === "suspended")
      audioContextRef.current.resume();

    // START COUNT-IN: speak beats 1..N then launch metronome
    const interval = 60000 / bpm;
    let n = 1;
    setCountIn(n);
    
    playSound(true, false); // accent click on each count-in beat

    countInRef.current = setInterval(() => {
      n += 1;
      if (n <= beats) {
        setCountIn(n);
      
        playSound(false, false);
      } else {
        // Count-in done → launch metronome
        clearInterval(countInRef.current!);
        setCountIn(null);
        beatCount.current = 0;
        subBeatCount.current = 0;
        setIsPlaying(true);
      }
    }, interval);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCountIn(null);
    if (countInRef.current) clearInterval(countInRef.current);
    if (typeof window !== "undefined" && window.speechSynthesis)
      window.speechSynthesis.cancel();
    setCurrentBeat(0);
    setCurrentSubBeat(0);
    beatCount.current = 0;
    subBeatCount.current = 0;
  };

  const handleBpmChange = (val: number) => {
    const clamped = Math.max(30, Math.min(300, val));
    setBpm(clamped);
    setBpmInput(String(clamped));
  };

  const getTempoLabel = (b: number) => {
    if (b < 60) return "Largo";
    if (b < 80) return "Adagio";
    if (b < 108) return "Andante";
    if (b < 132) return "Moderato";
    if (b < 168) return "Allegro";
    if (b < 200) return "Vivace";
    return "Presto";
  };

  /* pulse animation duration */
  const pulseDuration = `${(60 / bpm).toFixed(2)}s`;

  return (
    <div
      className="min-h-screen py-10 px-4 flex flex-col items-center justify-start"
      style={{
        backgroundColor: "var(--background)",
        fontFamily: "var(--font-roboto)",
      }}>
      <div className="w-full max-w-2xl space-y-5">
        {/* ── HEADER ── */}
        <header className="text-center mb-2">
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-4 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}>
            <Music className="w-4 h-4" />
            Công cụ nhịp điệu
          </div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tight"
            style={{
              fontFamily: "var(--font-montserrat)",
              color: "var(--foreground)",
            }}>
            Máy Đánh Nhịp
          </h1>
        </header>

        {/* ── PLAY CARD ── */}
        <div
          className="rounded-3xl border p-8 text-center relative overflow-hidden"
          style={{
            backgroundColor: "var(--card)",
            borderColor:
              isPlaying || countIn !== null
                ? "rgba(200,169,110,0.45)"
                : "var(--border)",
            boxShadow:
              isPlaying || countIn !== null
                ? "0 0 40px rgba(200,169,110,0.1)"
                : "0 4px 20px rgba(0,0,0,0.12)",
            transition: "border-color 0.3s, box-shadow 0.3s",
          }}>
          {/* Subtle bg radial when playing */}
          {(isPlaying || countIn !== null) && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(200,169,110,0.07) 0%, transparent 70%)",
              }}
            />
          )}

          {/* Beat indicators */}
          <div className="flex justify-center items-end gap-3 mb-8">
            {Array.from({ length: beats }, (_, i) => {
              const isActive = currentBeat === i && isPlaying;
              const isAccent = i === 0;
              return (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div
                    className="rounded-full transition-all duration-100"
                    style={{
                      width: isActive ? "18px" : "14px",
                      height: isActive ? "18px" : "14px",
                      backgroundColor: isActive
                        ? isAccent
                          ? "var(--brand-primary)"
                          : "var(--foreground)"
                        : "var(--border)",
                      boxShadow: isActive
                        ? isAccent
                          ? "0 0 16px rgba(200,169,110,0.7)"
                          : "0 0 12px rgba(255,255,255,0.3)"
                        : "none",
                    }}
                  />
                  {subdivision > 1 && (
                    <div className="flex gap-1">
                      {Array.from({ length: subdivision }, (_, si) => {
                        const sbIdx = i * subdivision + si;
                        const subActive = currentSubBeat === sbIdx && isPlaying;
                        return (
                          <div
                            key={si}
                            className="w-1.5 h-1.5 rounded-full transition-all duration-75"
                            style={{
                              backgroundColor: subActive
                                ? "var(--brand-primary)"
                                : "var(--border)",
                              opacity: subActive ? 1 : 0.5,
                            }}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* BPM display */}
          <div className="mb-7">
            <div
              className="relative inline-flex flex-col items-center justify-center w-36 h-36 rounded-full border-2 mx-auto"
              style={{
                borderColor:
                  countIn !== null
                    ? "rgba(200,169,110,0.7)"
                    : "rgba(200,169,110,0.3)",
                backgroundColor: "var(--background)",
                transition: "border-color 0.15s",
              }}>
              {/* Pulse ring when playing */}
              {isPlaying && (
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: "1.5px solid var(--brand-primary)",
                    animation: `metPulse ${pulseDuration} cubic-bezier(0.2, 0, 0.6, 1) infinite`,
                    opacity: 0,
                    animationDelay: "0.02s",
                  }}
                />
              )}
              {/* Count-in flash ring */}
              {countIn !== null && (
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    border: "2px solid var(--brand-primary)",
                    animation: `metPulse ${(60000 / bpm / 1000).toFixed(2)}s cubic-bezier(0.2, 0, 0.6, 1) infinite`,
                    opacity: 0,
                  }}
                />
              )}

              {countIn !== null ? (
                // COUNT-IN: show big number
                <>
                  <span
                    key={countIn}
                    className="text-6xl font-black leading-none"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      color: "var(--brand-primary)",
                      animation: "countPop 0.15s ease-out",
                    }}>
                    {countIn}
                  </span>
                  <span
                    className="text-xs font-bold uppercase tracking-widest mt-1"
                    style={{
                      color: "var(--muted-foreground)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    chuẩn bị
                  </span>
                </>
              ) : (
                // NORMAL: show BPM
                <>
                  <span
                    className="text-5xl font-black leading-none"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      color: "var(--foreground)",
                    }}>
                    {bpm}
                  </span>
                  <span
                    className="text-xs font-bold uppercase tracking-widest mt-1"
                    style={{
                      color: "var(--brand-primary)",
                      fontFamily: "var(--font-montserrat)",
                    }}>
                    BPM
                  </span>
                  <span
                    className="text-xs mt-0.5"
                    style={{ color: "var(--muted-foreground)" }}>
                    {getTempoLabel(bpm)}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Main controls */}
          <div className="flex items-center justify-center gap-5">
            {/* Reset */}
            <button
              onClick={handleReset}
              className="w-12 h-12 flex items-center justify-center rounded-full border transition-all duration-150 hover:scale-105 active:scale-95"
              style={{
                backgroundColor: "var(--background)",
                borderColor: "var(--border)",
                color: "var(--muted-foreground)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(200,169,110,0.4)";
                (e.currentTarget as HTMLElement).style.color =
                  "var(--brand-primary)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "var(--border)";
                (e.currentTarget as HTMLElement).style.color =
                  "var(--muted-foreground)";
              }}
              title="Đặt lại">
              <RotateCcw className="w-5 h-5" />
            </button>

            {/* Play / Pause — primary */}
            <button
              onClick={handlePlayPause}
              className="w-20 h-20 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-105 active:scale-95"
              style={{
                backgroundColor: "var(--brand-primary)",
                color: "var(--brand-primary-foreground)",
                boxShadow: isPlaying
                  ? "0 0 0 6px rgba(200,169,110,0.2), 0 4px 20px rgba(200,169,110,0.4)"
                  : "0 4px 20px rgba(200,169,110,0.3)",
              }}
              title={isPlaying ? "Dừng" : "Phát"}>
              {isPlaying ? (
                <Pause className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8 ml-1" />
              )}
            </button>

            {/* Mute */}
            <button
              onClick={() => setIsMuted((m) => !m)}
              className="w-12 h-12 flex items-center justify-center rounded-full border transition-all duration-150 hover:scale-105 active:scale-95"
              style={{
                backgroundColor: isMuted
                  ? "rgba(200,169,110,0.1)"
                  : "var(--background)",
                borderColor: isMuted
                  ? "rgba(200,169,110,0.5)"
                  : "var(--border)",
                color: isMuted
                  ? "var(--brand-primary)"
                  : "var(--muted-foreground)",
              }}
              title={isMuted ? "Bật tiếng" : "Tắt tiếng"}>
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
          </div>

          {/* Volume */}
          {!isMuted && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <Volume2
                className="w-3.5 h-3.5"
                style={{ color: "var(--muted-foreground)" }}
              />
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="slider w-28 h-1.5 rounded-full appearance-none cursor-pointer"
                style={{ backgroundColor: "var(--border)" }}
              />
              <span
                className="text-xs w-7 text-right"
                style={{ color: "var(--muted-foreground)" }}>
                {Math.round(volume * 100)}%
              </span>
            </div>
          )}
        </div>

        {/* ── BPM CONTROL CARD ── */}
        <div
          className="rounded-3xl border p-6"
          style={{
            backgroundColor: "var(--card)",
            borderColor: "var(--border)",
          }}>
          <p
            className="text-xs font-bold uppercase tracking-widest mb-5"
            style={{
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}>
            Tốc độ (BPM)
          </p>

          {/* Slider + manual input */}
          <div className="flex items-center gap-4 mb-5">
            <span
              className="text-xs w-6 text-center"
              style={{ color: "var(--muted-foreground)" }}>
              30
            </span>
            <input
              type="range"
              min="30"
              max="300"
              value={bpm}
              onChange={(e) => handleBpmChange(parseInt(e.target.value))}
              className="slider flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
              style={{ backgroundColor: "var(--border)" }}
            />
            <span
              className="text-xs w-8 text-center"
              style={{ color: "var(--muted-foreground)" }}>
              300
            </span>

            {/* Manual BPM input */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleBpmChange(bpm - 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-sm font-bold border transition-all"
                style={{
                  backgroundColor: "var(--background)",
                  borderColor: "var(--border)",
                  color: "var(--muted-foreground)",
                }}>
                −
              </button>
              <input
                type="number"
                min="30"
                max="300"
                value={bpmInput}
                onChange={(e) => setBpmInput(e.target.value)}
                onBlur={(e) => handleBpmChange(parseInt(e.target.value) || bpm)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  handleBpmChange(parseInt(bpmInput) || bpm)
                }
                className="w-14 text-center text-sm font-black rounded-lg py-1 outline-none border"
                style={{
                  backgroundColor: "var(--background)",
                  color: "var(--foreground)",
                  borderColor: "var(--border)",
                  fontFamily: "var(--font-montserrat)",
                  appearance: "textfield",
                }}
              />
              <button
                onClick={() => handleBpmChange(bpm + 1)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-sm font-bold border transition-all"
                style={{
                  backgroundColor: "var(--background)",
                  borderColor: "var(--border)",
                  color: "var(--muted-foreground)",
                }}>
                +
              </button>
            </div>
          </div>

          {/* Preset chips */}
          <div className="flex flex-wrap gap-2">
            {PRESET_TEMPOS.map((p) => {
              const isActive = bpm === p.bpm;
              return (
                <button
                  key={p.name}
                  onClick={() => handleBpmChange(p.bpm)}
                  className="px-3 py-1.5 rounded-full text-xs font-bold border transition-all duration-150"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    backgroundColor: isActive
                      ? "var(--brand-primary)"
                      : "rgba(200,169,110,0.06)",
                    color: isActive
                      ? "var(--brand-primary-foreground)"
                      : "var(--muted-foreground)",
                    borderColor: isActive
                      ? "var(--brand-primary)"
                      : "var(--border)",
                    boxShadow: isActive
                      ? "0 2px 10px rgba(200,169,110,0.25)"
                      : "none",
                  }}>
                  {p.name} <span style={{ opacity: 0.7 }}>{p.bpm}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── SETTINGS GROUP: Time sig + Subdivision ── */}
        <div className="grid grid-cols-2 gap-4">
          {/* Time signature */}
          <div
            className="rounded-3xl border p-5"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-4"
              style={{
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
              }}>
              Nhịp
            </p>
            <div className="grid grid-cols-2 gap-2">
              {TIME_SIGS.map((sig) => {
                const isActive = beats === sig;
                return (
                  <button
                    key={sig}
                    onClick={() => {
                      setBeats(sig);
                      handleReset();
                    }}
                    className="py-2.5 rounded-xl text-sm font-black border transition-all duration-150"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: isActive
                        ? "var(--brand-primary)"
                        : "var(--background)",
                      color: isActive
                        ? "var(--brand-primary-foreground)"
                        : "var(--muted-foreground)",
                      borderColor: isActive
                        ? "var(--brand-primary)"
                        : "var(--border)",
                    }}>
                    {sig}/{sig === 6 ? "8" : "4"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Subdivision */}
          <div
            className="rounded-3xl border p-5"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "var(--border)",
            }}>
            <p
              className="text-xs font-bold uppercase tracking-widest mb-4"
              style={{
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
              }}>
              Nhịp điệu
            </p>
            <div className="grid grid-cols-2 gap-2">
              {RHYTHM_PATTERNS.map((pat) => {
                const isActive = subdivision === pat.subdivision;
                return (
                  <button
                    key={pat.name}
                    onClick={() => {
                      setSubdivision(pat.subdivision);
                      handleReset();
                    }}
                    className="py-2.5 px-2 rounded-xl text-xs font-bold border transition-all duration-150 leading-snug"
                    style={{
                      fontFamily: "var(--font-montserrat)",
                      backgroundColor: isActive
                        ? "var(--brand-primary)"
                        : "var(--background)",
                      color: isActive
                        ? "var(--brand-primary-foreground)"
                        : "var(--muted-foreground)",
                      borderColor: isActive
                        ? "var(--brand-primary)"
                        : "var(--border)",
                    }}>
                    {pat.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px; height: 16px;
          border-radius: 50%;
          background: var(--brand-primary);
          cursor: pointer;
          border: 2px solid var(--background);
          box-shadow: 0 0 6px rgba(200,169,110,0.4);
        }
        .slider::-moz-range-thumb {
          width: 16px; height: 16px;
          border-radius: 50%;
          background: var(--brand-primary);
          cursor: pointer;
          border: 2px solid var(--background);
        }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; }
        @keyframes metPulse {
          0%   { transform: scale(1);    opacity: 0.5; }
          60%  { transform: scale(1.25); opacity: 0.15; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes countPop {
          0%   { transform: scale(1.4); opacity: 0.5; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MetronomeClient;
